import { Box, Stack } from '@mui/material';
import React from 'react';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { ContentReviewInterface } from '../../models/ContentReviewInterface';
import { Palette } from 'Palette';
import { StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';
import ImageCarousel from '../template/ImageCarousel';
import LeftRightList from '../template/LeftRightList';
import StandardHeaderText from '../template/StandardHeaderText';
import Subtitle from '../template/Subtitle';
import Table from '../template/Table';
import TextBlock from '../template/TextBlock';
import HeroImage from '../template/HeroImage';
import CommentIconComponent from './CommentIcon';

interface ReviewContentReadViewProps {
  publishedContent: ContentManagementInterface;
  reviewData: ContentReviewInterface;
  modelType?: 'FundOpportunity' | 'FundUpdate';
  tone?: keyof typeof Palette;
}

/**
 * Read-only view of the fund opportunity content
 * Renders content sections as they appear in FundOpportunityEOI but in read-only mode
 */
/** Text sections and the single-element blocks share one review card; only the block inside differs. */
function renderTextFamilyBlock(item: any, tone: React.ComponentProps<typeof Subtitle>['tone']) {
  if (item.type === 'Subtitle') {
    return <Subtitle {...item.properties} id={item.id} tone={tone} />;
  }
  if (item.type === 'Text') {
    return <TextBlock {...item.properties} id={item.id} tone={tone} />;
  }
  if (item.type === 'HeroImage') {
    return <HeroImage {...item.properties} id={item.id} tone={tone} />;
  }
  return <StandardHeaderText {...item.properties} id={item.id} editMode={false} tone={tone} />;
}

export default function ReviewContentReadView({
  publishedContent,
  reviewData,
  modelType = 'FundOpportunity',
  tone = 'tone1',
}: ReviewContentReadViewProps) {
  const renderContentItem = (item: any, index: number) => {
    const sectionId = `section-${index}`;

    // Check if there's a proposed change for this section
    const change = reviewData.changes.find((c) => c.sectionId === sectionId);
    const sectionComments = reviewData.comments.filter((c) => c.sectionId === sectionId);

    // Use changed content if available, otherwise use published
    const contentToRender = change?.changed ? change.contentAfter : item;

    switch (contentToRender.type) {
      case 'StandardHeaderText':
      case 'Subtitle':
      case 'Text':
      case 'HeroImage':
        return (
          <Box key={sectionId} data-section-id={sectionId} sx={{ mb: 3, position: 'relative' }}>
            {change?.changed && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: sectionComments.length > 0 ? 120 : -8, // Position left of comment icon if comments exist
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  zIndex: 1,
                }}
              >
                Changed
              </Box>
            )}
            {renderTextFamilyBlock(contentToRender, tone)}
            {/* Comment overlay for this section */}
            {sectionComments.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 21,
                  maxWidth: '200px', // Limit width to prevent overflow
                }}
              >
                <CommentIconComponent conversations={sectionComments} tone={tone} />
              </Box>
            )}
          </Box>
        );

      case 'ImageCarousel':
        return (
          <Box key={sectionId} data-section-id={sectionId} sx={{ mb: 3, position: 'relative' }}>
            {change?.changed && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: sectionComments.length > 0 ? 120 : -8, // Position left of comment icon if comments exist
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  zIndex: 1,
                }}
              >
                Changed
              </Box>
            )}
            <ImageCarousel
              {...contentToRender.properties}
              id={contentToRender.id}
              modelId={publishedContent.relatedObjectId}
              modelType={modelType}
              editMode={false}
              tone={tone}
            />
            {/* Comment overlay for this section */}
            {sectionComments.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 21,
                  maxWidth: '200px', // Limit width to prevent overflow
                }}
              >
                <CommentIconComponent conversations={sectionComments} tone={tone} />
              </Box>
            )}
          </Box>
        );

      case 'LeftRightList':
        return (
          <Box key={sectionId} data-section-id={sectionId} sx={{ mb: 3, position: 'relative' }}>
            {change?.changed && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: sectionComments.length > 0 ? 120 : -8, // Position left of comment icon if comments exist
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  zIndex: 1,
                }}
              >
                Changed
              </Box>
            )}
            <LeftRightList
              {...contentToRender.properties}
              id={contentToRender.id}
              editMode={false}
              tone={tone}
            />
            {/* Comment overlay for this section */}
            {sectionComments.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 21,
                  maxWidth: '200px', // Limit width to prevent overflow
                }}
              >
                <CommentIconComponent conversations={sectionComments} tone={tone} />
              </Box>
            )}
          </Box>
        );

      case 'Table':
        return (
          <Box key={sectionId} data-section-id={sectionId} sx={{ mb: 3, position: 'relative' }}>
            {change?.changed && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: sectionComments.length > 0 ? 120 : -8, // Position left of comment icon if comments exist
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  zIndex: 1,
                }}
              >
                Changed
              </Box>
            )}
            <Table
              {...contentToRender.properties}
              id={contentToRender.id}
              editMode={false}
              tone={tone}
            />
            {/* Comment overlay for this section */}
            {sectionComments.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 21,
                  maxWidth: '200px', // Limit width to prevent overflow
                }}
              >
                <CommentIconComponent conversations={sectionComments} tone={tone} />
              </Box>
            )}
          </Box>
        );

      default:
        return (
          <StyledCard
            key={sectionId}
            data-section-id={sectionId}
            tone={tone}
            sx={{ mb: 2, position: 'relative' }}
          >
            <StyledCardContent>
              <StyledTypography color="text.secondary">
                Unknown content type: {contentToRender.type}
              </StyledTypography>
            </StyledCardContent>
            {/* Comment overlay for this section */}
            {sectionComments.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 21,
                  maxWidth: '200px', // Limit width to prevent overflow
                }}
              >
                <CommentIconComponent conversations={sectionComments} tone={tone} />
              </Box>
            )}
          </StyledCard>
        );
    }
  };

  return (
    <StyledCard tone={tone} sx={{ overflow: 'visible' }}>
      <StyledCardContent sx={{ overflow: 'visible' }}>
        <StyledTypography tone={tone} variant="h6" sx={{ mb: 3 }}>
          Content Preview
        </StyledTypography>

        <Stack spacing={2} sx={{ position: 'relative', overflow: 'visible' }}>
          {publishedContent.content.map((item, index) => renderContentItem(item, index))}
        </Stack>
      </StyledCardContent>
    </StyledCard>
  );
}
